// The cleaning-cycle strips: how a rule's interval lands on the nights of a
// stay or the days a room stands empty, and the strip that draws it.
import React from "react";
import { Rule, absorbedBy } from "./api";
import { Chip, byRank, codes } from "./GuideChips";
export interface Mark {
  rule: Rule;
  // The deepest due rule that both outranks this one and covers it, if any.
  absorber: Rule | null;
}
export interface Cell {
  label: string;
  spoken: string;
  anchor: boolean;
  due: Mark[];
}
// A strip is capped so it stays readable at the longest interval the rules
// allow, so it also reports the cleans whose first due day falls past its end.
export interface StripPlan {
  cells: Cell[];
  outside: Rule[];
  lastLabel: string;
}
// The projector only lets a clean absorb another when it outranks it AND its
// coverage names it, so a same-night collision without that coverage really
// does put both cleans on the sheet.
export function marks(due: Rule[]): Mark[] {
  const ordered = byRank(due);
  return ordered.map((rule) => {
    const absorbers = byRank(absorbedBy(ordered, rule)).filter(
      (other) => other.rank > rule.rank,
    );
    return { rule, absorber: absorbers[absorbers.length - 1] ?? null };
  });
}
export function cycleRules(rules: Rule[], trigger: string): Rule[] {
  return rules.filter(
    (rule) => rule.trigger === trigger && (rule.intervalNights ?? 0) > 0,
  );
}
// A rule with interval n is anchored on the arrival date, so the first due date
// is anchor + n. The strip then simulates every due service completed on time:
// each completion re-anchors every rule it explicitly covers to that date, so a
// deeper clean on a different cadence moves the lighter clean's next due.
function simulatedDue(
  cycles: Rule[],
  offsets: number[],
): Map<number, Rule[]> {
  const anchors = new Map<string, number>();
  const dueByOffset = new Map<number, Rule[]>();
  for (const offset of offsets) {
    const due =
      offset === 0
        ? []
        : cycles.filter((rule) => {
            const anchor = anchors.get(rule.id) ?? 0;
            const since = offset - anchor;
            return (
              since > 0 &&
              (rule.intervalNights as number) > 0 &&
              since % (rule.intervalNights as number) === 0
            );
          });
    dueByOffset.set(offset, due);
    // Only performed cleans move the cadence: an absorbed (struck-through)
    // collision mark was settled by its absorber and was not performed itself,
    // so only the non-absorbed actionable marks re-anchor their coverage.
    // Displayed collision marks are preserved; this only gates the reset.
    for (const completed of marks(due).filter((mark) => mark.absorber === null).map((mark) => mark.rule))
      for (const covered of completed.coverageRuleIds ?? [])
        anchors.set(covered, offset);
  }
  return dueByOffset;
}
export function occupiedStrip(rules: Rule[]): StripPlan {
  const arrivals = rules.filter((rule) => rule.trigger === "arrival");
  const cycles = cycleRules(rules, "occupiedInterval");
  const longest = Math.max(
    0,
    ...cycles.map((rule) => rule.intervalNights ?? 0),
  );
  const nights = Math.min(15, Math.max(1, longest * 2 + 1));
  const offsets = Array.from({ length: nights }).map((_, index) => index);
  const dueByOffset = simulatedDue(cycles, offsets);
  return {
    cells: Array.from({ length: nights })
      .map((_, index) => index + 1)
      .map((night) => ({
        label: `N${night}`,
        spoken: `night ${night}`,
        anchor: night === 1,
        due: marks(
          night === 1 ? arrivals : (dueByOffset.get(night - 1) ?? []),
        ),
      })),
    outside: byRank(
      cycles.filter((rule) => (rule.intervalNights as number) + 1 > nights),
    ),
    lastLabel: `night ${nights}`,
  };
}
// A vacant rule counts from the checkout, so it falls due on + n, + 2n, and so on.
export function vacantStrip(rules: Rule[]): StripPlan {
  const departures = rules.filter((rule) => rule.trigger === "departure");
  const cycles = cycleRules(rules, "vacantInterval");
  const longest = Math.max(
    0,
    ...cycles.map((rule) => rule.intervalNights ?? 0),
  );
  const days = Math.min(11, Math.max(1, longest * 2 + 1));
  const offsets = Array.from({ length: days }).map((_, day) => day);
  const dueByOffset = simulatedDue(cycles, offsets);
  return {
    cells: Array.from({ length: days }).map((_, day) => ({
      label: day === 0 ? "Checkout" : `+${day}`,
      spoken: day === 0 ? "the checkout day" : `day +${day}`,
      anchor: day === 0,
      due: marks(
        day === 0 ? departures : (dueByOffset.get(day) ?? []),
      ),
    })),
    outside: byRank(
      cycles.filter((rule) => (rule.intervalNights as number) > days - 1),
    ),
    lastLabel: `+${days - 1}`,
  };
}
export function Truncation({ plan }: { plan: StripPlan }) {
  if (plan.outside.length === 0) return null;
  return (
    <>
      {" "}
      This strip stops at {plan.lastLabel}. Cleans that fall due later than that
      are not marked here: {codes(plan.outside)}.
    </>
  );
}
export function Strip({ plan }: { plan: StripPlan }) {
  return (
    <div className="hk-guide-strip-wrap">
      <div className="hk-guide-strip">
        {plan.cells.map((cell) => (
          <div
            className={`hk-guide-day ${cell.anchor ? "hk-guide-anchor" : ""}`}
            key={cell.label}
          >
            <div className="hk-guide-day-n">{cell.label}</div>
            <div className="hk-guide-mark">
              {cell.due.length === 0 ? (
                <span className="hk-guide-none">·</span>
              ) : (
                cell.due.map((mark) => (
                  <Chip
                    key={mark.rule.id}
                    rule={mark.rule}
                    struck={mark.absorber !== null}
                  />
                ))
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
